import React, { useEffect, useState } from 'react';
import { X, Sparkles, Loader2, Plus } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { joinPersonName, sanitizeNamePart } from '../../../lib/nameUtils';
import { STAGE_ORDER, STAGE_LABELS } from '../../../lib/workflow/transitions';
import SearchableSelect from '../../shared/SearchableSelect';

interface CreateInternalTaskModalProps {
  uid: string;
  applicationId: string;
  applicantName: string;
  /** The applicant's current stage — the phase the task is stamped with
   *  by default. The admin may choose a different phase in the form;
   *  the server validates the value and stamps exactly that phase. */
  currentStage?: string;
  onClose: () => void;
  onCreated: () => void;
}

interface AdminOption {
  uid: string;
  displayName: string;
  email: string;
}

/**
 * Create an internal (admin-only) task for an applicant. Manual entry, or
 * AI-suggested from the applicant's pending stage prerequisites.
 * Rendered from the drawer's gate panel as an admin action row.
 */
export const CreateInternalTaskModal: React.FC<CreateInternalTaskModalProps> = ({
  uid,
  applicationId,
  applicantName,
  currentStage,
  onClose,
  onCreated,
}) => {
  const [details, setDetails] = useState('');
  const [assignee, setAssignee] = useState('');
  const [completeBy, setCompleteBy] = useState('');
  const [stageStamp, setStageStamp] = useState<string>(
    currentStage && (STAGE_ORDER as readonly string[]).includes(currentStage) ? currentStage : 'draft'
  );
  const [admins, setAdmins] = useState<AdminOption[]>([]);
  const [adminsFailed, setAdminsFailed] = useState(false);

  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<{ details: string; sourceGateKey: string | null }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: any = await safeFetch('/api/admin/users');
        const list: AdminOption[] = (data.users || data || [])
          .filter((u: any) => ['admin', 'platform_admin', 'owner', 'super', 'manager'].includes(String(u.role || '').toLowerCase()))
          .map((u: any) => ({
            uid: u.uid,
            // PR-16: sanitize — never stringify raw name-field shapes.
            displayName: sanitizeNamePart(u.displayName) || joinPersonName([u.firstName, u.lastName]) || u.email,
            email: u.email || '',
          }));
        if (!cancelled) setAdmins(list);
      } catch {
        if (!cancelled) setAdminsFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const data: any = await safeFetch('/api/admin/workflow/internal-tasks/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, applicationId }),
      });
      setSuggestions(data.suggestions || []);
      if ((data.suggestions || []).length === 0) {
        notifyToast('info', 'No suggestions — the stage looks clear. You can still create a manual task.');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  const createTask = async (taskDetails: string, sourceGateKey: string | null, aiSuggested: boolean) => {
    if (!taskDetails.trim()) {
      notifyToast('warning', 'Task details are required');
      return;
    }
    setSaving(true);
    try {
      await safeFetch('/api/admin/workflow/internal-tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          details: taskDetails.trim(),
          assigneeAdminId: assignee || null,
          linkedApplicationId: applicationId,
          completeBy: completeBy || null,
          sourceGateKey,
          aiSuggested,
          stageStamp,
        }),
      });
      notifyToast('success', 'Internal task created');
      onCreated();
      onClose();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9400] flex items-center justify-center p-4 bg-slate-900/50" role="dialog" aria-modal="true" aria-label="Create internal task" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">Create internal task</h3>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">
              {applicantName} <span className="font-mono">#{applicationId}</span> · admin-only
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 cursor-pointer" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <button
            type="button"
            onClick={handleSuggest}
            disabled={suggesting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-bold cursor-pointer"
          >
            {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {suggesting ? 'Suggesting…' : 'AI-suggest from pending items'}
          </button>

          {suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Suggestions — click to create</p>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => createTask(s.details, s.sourceGateKey, true)}
                  disabled={saving}
                  className="w-full text-left flex items-start gap-2 rounded-xl border border-violet-200 bg-violet-50/60 px-3 py-2.5 hover:bg-violet-50 disabled:opacity-50 cursor-pointer"
                >
                  <Plus className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" />
                  <span className="text-xs font-semibold text-slate-800">{s.details}</span>
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 pt-4 space-y-3">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Or create manually</p>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Task details</label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 resize-y"
                placeholder="What needs to be done…"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Phase</label>
              <select
                value={stageStamp}
                onChange={(e) => setStageStamp(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-2 py-2 text-xs font-semibold text-slate-700 cursor-pointer"
                aria-label="Phase this task belongs to"
              >
                {STAGE_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {(STAGE_LABELS as any)[s] || s}
                    {currentStage === s ? ' (current)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                The task keeps this phase stamp even after the applicant moves on.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Assign to</label>
                {adminsFailed ? (
                  <p className="text-[11px] text-slate-500">Admin list unavailable — task will be unassigned.</p>
                ) : (
                  <SearchableSelect
                    value={assignee}
                    onChange={(v) => setAssignee(v)}
                    options={admins.map((a) => ({ value: a.uid, label: a.displayName }))}
                    placeholder="Search team members…"
                    clearLabel="Unassigned"
                    emptyLabel="No team members found"
                  />
                )}
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Complete by</label>
                <input
                  type="date"
                  value={completeBy}
                  onChange={(e) => setCompleteBy(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-2 py-2 text-xs text-slate-700"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => createTask(details, null, false)}
              disabled={saving || !details.trim()}
              className="w-full px-4 py-2.5 rounded-xl bg-brand hover:bg-brand-active disabled:opacity-50 text-white text-xs font-bold cursor-pointer"
            >
              {saving ? 'Creating…' : 'Create task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
